<template>
    <div id="app" :class="(dataLoaded ? 'h-auto' : 'vh-100')">
        <header>
            <app-header />
        </header>

        <div
            class="container-fluid py-4 px-5"
            :class="uriParams.type + '-evaluations'"
            v-if="isErrorWhileReadingDataOccurred === false"
        >
            <b-spinner
                variant="primary"
                v-if="system !== null"
                v-show="
                    system.isDbReady == null &&
                    system.isEvaluationsDataExists == null &&
                    system.contextItemsLoaded == null &&
                    system.dataLoaded == null
                "
            />

            <warning
                v-if="system !== null"
                v-show="system.isDbReady && !system.isEvaluationsDataExists && system.isEvaluationsDataExists != null"
                :text="this.$i18n.NO_DATA"
            />

            <warning
                v-if="system !== null"
                v-show="!system.isDbReady"
                :text="this.$i18n.CALCULATIONS_ARE_BEING_PROCESSED"
            />

            <page-title :title="this.title" />

            <div class="grid-item" id="data-context">
                <context :eval-type="uriParams.type" :show-national-data-toggler="isNationalDataNeeded" />
            </div>

            <div class="grid-item" id="data-filter">
                <queryFilter  ref="queryFilters" :module="this.getModule()" />
            </div>

            <router-view :name=dataPresentationMode v-on:toggleQueryFilters="toggleQueryFilters" />

            <div class="grid-item" id="data-meta">
                <further-options v-if="dataLoaded" />
            </div>
            <footer>
                <latest-data-import class="pb-3" v-if="dataLoaded" />
            </footer>
        </div>
        <div v-else-if="isErrorWhileReadingDataOccurred" class="container-fluid py-3">
            <warning :id="'unexpected-error-warning'" :title="this.$i18n.INFORMATION" :text="errorMessage" />
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

import appHeader from "./components/common/app-header";
import { BSpinner } from "bootstrap-vue";
import warning from "./components/common/warning";
import pageTitle from "./components/common/page-title";
import context from "./components/common/context";
import queryFilter from "./components/common/query-filters/query-filters";
import furtherOptions from "./components/common/further-options";
import latestDataImport from "./components/common/latest-data-import";

import jwtDecode from "jwt-decode";

export default {
    data: {
        dataPresentationMode: process.env.VUE_APP_DATA_PRESENTATION_MODE
    },
    components: {
        appHeader,
        BSpinner,
        warning,
        pageTitle,
        context,
        queryFilter: queryFilter,
        furtherOptions,
        latestDataImport,
    },
    computed: {
        isNationalDataNeeded: function() { return this.dataPresentationMode == 'list' ? false : true; },
        ...mapState("dataModule", {
            system: (state) => state.system,
            isErrorWhileReadingDataOccurred: (state) => state.isErrorWhileReadingDataOccurred,
            errorMessage: (state) => state.errorMessage,
        }),
        ...mapState("tumourDataModule", {
            tumourDataLoaded: (state) => state.dataLoaded,
        }),
        ...mapState("rhinoDataModule", {
            rhinoDataLoaded: (state) => state.dataLoaded,
        }),
        ...mapState("ciDataModule", {
            ciDataLoaded: (state) => state.dataLoaded,
        }),
        ...mapState("tagDataModule", {
            tagDataLoaded: (state) => state.dataLoaded,
        }),
        ...mapState("patientsListDataModule", {
            patientsListDataLoaded: (state) => state.dataLoaded,
        }),
        ...mapState("contextModule", {
            context: (state) => state.context,
            contextItemsLoaded: (state) => state.contextItemsLoaded,
        }),
        ...mapState({
            aoqnUserId: (state) => state.aoqnUserId,
        }),
        uriParams: function () {
            // get params from query string
            const uri = window.location.search.substring(1);
            const params = new URLSearchParams(uri);

            return { type: params.get("type"), token: params.get("token"), fileName: params.get("fileName") };
        },
        dataLoaded: function () {
            let dataLoaded = null;

            let type = this.uriParams.type;
            if (type === null) type = sessionStorage.evaluationsType;

            switch (type) {
                case "tumour":
                    dataLoaded = this.tumourDataLoaded;
                    break;
                case "rhino":
                    dataLoaded = this.rhinoDataLoaded;
                    break;
                case "ci":
                    dataLoaded = this.ciDataLoaded;
                    break;
                case "tag":
                    dataLoaded = this.tagDataLoaded;
                    break;
                case "dghnoCi":
                    dataLoaded = this.patientsListDataLoaded;
                    break;
            }
            return dataLoaded;
        },
        title: function () {
            let title = "";
            switch (this.uriParams.type) {
                case "tumour":
                    title = this.$i18n.TUMOUR_EVALUATIONS;
                    break;
                case "rhino":
                    title = this.$i18n.RHINO_EVALUATIONS;
                    break;
                case "ci", "dghnoCi":
                    title = this.$i18n.CI_EVALUATIONS;
                    break;
                case "tag":
                    title = this.$i18n.TAG_EVALUATIONS;
                    break;
            }
            return title;
        }
    },
    methods: {
        ...mapActions(["setQueryDisplayValues", "setTimezoneOffset", "setAoqnUserId", "setAoqnUserRole"]),
        ...mapActions("tumourDataModule", ["readNationalData"]),
        ...mapActions([
            "tumourDataModule/readMrAttributes",
            "rhinoDataModule/readMrAttributes",
            "ciDataModule/readMrAttributes",
            "tagDataModule/readMrAttributes",
            "patientsListDataModule/readMrAttributes"
        ]),
        ...mapActions("dataModule", ["readSystemData", "setIsErrorWhileReadingDataOccurred"]),
        getModule: function () {
            if (this.uriParams.type == 'download') {
                return 'download';
            }

            return this.dataPresentationMode != 'list' ? this.uriParams.type : 'patientsList';
        },
        auth() {
            // auth token
            let token = "";
            // evaluations type
            let type = "";

            const tokenFromQueryString = this.uriParams.token;
            const typeFromQueryString = this.uriParams.type;
            const fileNameFromQueryString = this.uriParams.fileName;

            if (tokenFromQueryString !== null && typeFromQueryString !== null) {
                token = tokenFromQueryString;
                type = typeFromQueryString;
            } else if (
                sessionStorage.jwt !== undefined &&
                sessionStorage.jwt.length !== 0 &&
                sessionStorage.evaluationsType !== undefined &&
                sessionStorage.evaluationsType.length !== 0
            ) {
                this.uriParams.token = sessionStorage.jwt;
                token = sessionStorage.jwt;
                this.uriParams.type = sessionStorage.evaluationsType;
                type = sessionStorage.evaluationsType;
            } else {
                window.location.href = `${process.env.VUE_APP_MAIN_MODULE_URL}/Evaluations/Index${window.location.pathname}`;
            }

            // both variables goes to sessionStorage for future use
            sessionStorage.jwt = token;
            // currently used for redirection
            sessionStorage.evaluationsType = type;

            sessionStorage.dataPresentationMode = this.dataPresentationMode;

            // decoding the token body, to retrieve
            // aoqan user id and role, timezone offset
            const body = jwtDecode(token);

            this.setAoqnUserId(body.UserID);
            this.setAoqnUserRole(body.Role);
            this.setTimezoneOffset(body.TimezoneOffset);

            // rethink
            this.setQueryDisplayValues({});

            // removing type and token from url
            if (fileNameFromQueryString) {
                this.$router.push(`${type}/${fileNameFromQueryString}`);
            }
            else {
                this.$router.push(`${type}`);
            }
        },
        readData() {
            let module = this.getModule();
            this.readSystemData(this.aoqnUserId)
                .then((res) => {
                    if (!this.system.isDbReady || !this.system.isEvaluationsDataExists) {
                        this.setIsErrorWhileReadingDataOccurred({
                            occurred: true,
                            message: this.$i18n.ERROR_WHILE_READING_DATA_OCCURRED,
                        });
                    } else {
                        this.setIsErrorWhileReadingDataOccurred({ occurred: false, message: "" });

                        this[`${module}DataModule/readMrAttributes`]()
                            .catch((error) => {
                                this.setIsErrorWhileReadingDataOccurred({
                                    occurred: true,
                                    message: this.$i18n.ERROR_WHILE_READING_DATA_OCCURRED,
                                });
                            })
                            .then((res) => {
                                this.setIsErrorWhileReadingDataOccurred({
                                    occurred: false,
                                    message: this.$i18n.ERROR_WHILE_READING_DATA_OCCURRED,
                                });
                            });
                    }
                });
        },
        toggleQueryFilters: function() { this.$refs.queryFilters.toggleState(); }
    },
    mounted() {
        this.auth();
        let module = this.getModule();
        if (module == 'download') {
            this.setIsErrorWhileReadingDataOccurred({ occurred: false, message: "" });
        }
        else {
            this.readData();
        }
    }
};
</script>

<style>
html {
    min-height: 100%;
}
body {
    min-height: 100%;
    display: flex;
}

.label-blue {
    color: #009efb;
}

.card {
    border: 0 !important;
    border-radius: 0 !important;
    margin-top: 20px;
    margin-bottom: 20px;
    padding: 20px;
}

.nav-link {
    color: #464a4c;
}

.tabs-wrapper,
.nav-tabs .nav-link.active {
    background: none;
}

.nav-tabs .nav-link.active {
    border-color: #cbd9dc #cbd9dc #f2f7f8;
}

.nav-tabs .nav-link.active {
    background: none !important;
}

.nav-tabs .nav-link:hover {
    border-color: #fff;
}

body {
    margin: 0;
}
header {

    height: 4.3rem;
}
#app {
    width: 100%;
    max-width: 1300px;
    margin: 0 auto;
    box-shadow: 0 0 60px rgba(0, 0, 0, 0.1);
    background-color: #f2f7f8;
    font-family: "Rubik", sans-serif;
    color: #54667a;
    font-weight: 300;
    background-color: #f2f7f8;
}

#app .card {
    font-size: 14px !important;
}

.navbar-header {
    width: 240px;
    flex-shrink: 0;
}

.navbar-header .navbar-brand {
    padding-top: 0px;
}

.navbar-header {
    line-height: 65px;
    text-align: center;
}
.navbar-header .navbar-brand {
    margin-right: 0px;
    padding-bottom: 0px;
    padding-top: 0px;
}
.navbar-header .navbar-brand .light-logo {
    display: none;
}
.navbar-header .navbar-brand b {
    line-height: 70px;
    display: inline-block;
}
</style>
